import { IStore } from 'redux/IStore';
import { IUserShippingAddresses, IUserShippingAddressesAction } from 'models/user/shipping/usershippingaddresses';
import { IUserShippingAddress } from 'models/user/shipping/usershippingaddress';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'usershippingaddresses/REQUEST_START';
export const REQUEST_FAILURE: string = 'usershippingaddresses/REQUEST_FAILURE';
export const GET_USER_SHIPPING_ADDRESSES_SUCCESS: string = 'usershippingaddresses/GET_USER_SHIPPING_ADDRESSES_SUCCESS';

/** Initial State */
const initialState: IUserShippingAddresses = {
	items: [],
	isFetching: false,
	loaded: false,
};

/** Reducer */
export function userShippingAddressesReducer(state = initialState, action: IUserShippingAddressesAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case GET_USER_SHIPPING_ADDRESSES_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				items: action.payload.items,
				loaded: true,
			});

		default:
			return state;
	}
}

/** Async Action Creator */
export function getUserShippingAddresses() {
	return (dispatch, getState: () => IStore): Promise<any> => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/ShippingAddresses',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => dispatch(getUserShippingAddressesSuccess(res)));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function setDefaultShippingAddress(addressId: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/ShippingAddress/' + addressId + '/SetDefault',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(updateDefaultAddressInList(addressId));
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_User_Shipping_ShippingAddresses_SetDefaultAddressSuccess'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function updateDefaultAddressInList(addressId: string) {
	return (dispatch, getState: () => IStore) => {
		let updatedShippingAddresses = [];
		const shippingAddresses = getState().userShippingAddresses.items;
		if (shippingAddresses && shippingAddresses.length > 0) {
			updatedShippingAddresses = shippingAddresses.map((address: IUserShippingAddress) => {
				address.isDefault = address.id === addressId;
				return address;
			});
		}
		dispatch(getUserShippingAddressesSuccess(updatedShippingAddresses));
	};
}

/** Async Action Creator */
export function deleteShippingAddress(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		return fetch(
			getState().fetchDefault.baseAPI + 'api/User/ShippingAddress/' + id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(deleteShippingAddressFromList(id));
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_UserShippingAddress_Deleted'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function deleteShippingAddressFromList(id: string) {
	return (dispatch, getState: () => IStore) => {
		const items: IUserShippingAddress[] = getState().userShippingAddresses.items;
		const index = items.indexOf(items.find((x: IUserShippingAddress) => x.id === id));
		items.splice(index, 1);
		dispatch(getUserShippingAddressesSuccess(items));
	};
}

/** Action Creator */
export function requestStart(): IUserShippingAddressesAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IUserShippingAddressesAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getUserShippingAddressesSuccess(items: IUserShippingAddress[]): IUserShippingAddressesAction {
	return {
		type: GET_USER_SHIPPING_ADDRESSES_SUCCESS,
		payload: {
			items,
		},
	};
}
